import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Stack,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Upload,
  FileText,
  Users,
  Trash2,
  Check,
  AlertCircle,
  Download,
  ArrowRight,
  ArrowLeft,
  Filter,
} from 'lucide-react';
import { Fee, Student, PreAssessedFee, PricingTier, FeeStatus, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';
import { getActiveSchools, getSchoolById } from '../../mock/schools';

interface FeeAssessmentManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface AssessmentDraft {
  id: string;
  studentId: string;
  student: Student;
  quantity: number;
  dueDate: string;
  notes?: string;
  pricingTier?: PricingTier;
}

const darkButtonSx = {
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  '&:hover': {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  },
};

export function FeeAssessmentManagement({ permissions }: FeeAssessmentManagementProps) {
  const { students: mockStudents, fees: mockFees, preAssessedFees: mockPreAssessedFees, setPreAssessedFees } = useData();
  const schools = getActiveSchools();
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [selectedFee, setSelectedFee] = useState<Fee | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [pasteInput, setPasteInput] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [assessmentDrafts, setAssessmentDrafts] = useState<AssessmentDraft[]>([]);
  const [defaultQuantity, setDefaultQuantity] = useState(1);
  const [defaultDueDate, setDefaultDueDate] = useState<string>(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [defaultNotes, setDefaultNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [schoolFilter, setSchoolFilter] = useState<string>('all');

  const filteredStudents = useMemo(() => {
    return mockStudents.filter((student) => {
      const matchesGrade = gradeFilter === 'all' || student.grade.toString() === gradeFilter;
      const matchesSchool = schoolFilter === 'all' || student.schoolId === schoolFilter;
      return matchesGrade && matchesSchool;
    });
  }, [mockStudents, gradeFilter, schoolFilter]);

  const steps = ['Select Fee', 'Select Students', 'Configure Settings', 'Review & Confirm'];

  const activeFees = useMemo(() => mockFees.filter(f => f.isActive), []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedStudents([]);
    setPasteInput('');
    setCsvFile(null);
  };

  const handleProcessPaste = () => {
    const studentIds = pasteInput
      .trim()
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    const foundStudents = mockStudents.filter(student =>
      studentIds.some(id =>
        student.studentIdNumber.toLowerCase() === id.toLowerCase()
      )
    );

    setSelectedStudents(foundStudents);
    setErrorMessage(
      foundStudents.length === 0
        ? 'No matching students found. Please check your input.'
        : foundStudents.length < studentIds.length
        ? `Found ${foundStudents.length} out of ${studentIds.length} students.`
        : ''
    );
  };

  const handleProcessCSV = async () => {
    if (!csvFile) return;

    const text = await csvFile.text();
    const studentIds = text
      .trim()
      .split(',')
      .map(id => id.trim().replace(/"/g, ''))
      .filter(id => id.length > 0);

    const foundStudents = mockStudents.filter(student =>
      studentIds.some(id =>
        student.studentIdNumber.toLowerCase() === id.toLowerCase()
      )
    );

    setSelectedStudents(foundStudents);
    setErrorMessage(
      foundStudents.length === 0
        ? 'No matching students found in CSV file.'
        : foundStudents.length < studentIds.length
        ? `Found ${foundStudents.length} out of ${studentIds.length} students.`
        : ''
    );
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedFee) {
      setErrorMessage('Please select a fee before continuing.');
      return;
    }
    if (activeStep === 1 && selectedStudents.length === 0) {
      setErrorMessage('Please select at least one student before continuing.');
      return;
    }
    if (activeStep === 2) {
      handleCreateDrafts();
    }
    setErrorMessage('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setErrorMessage('');
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedFee(null);
    setSelectedStudents([]);
    setPasteInput('');
    setCsvFile(null);
    setAssessmentDrafts([]);
    setDefaultQuantity(1);
    setDefaultDueDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setDefaultNotes('');
    setErrorMessage('');
  };

  const handleCreateDrafts = () => {
    if (!selectedFee || selectedStudents.length === 0) {
      setErrorMessage('Please select a fee and at least one student.');
      return;
    }

    const drafts: AssessmentDraft[] = selectedStudents.map(student => ({
      id: uuidv4(),
      studentId: student.id,
      student,
      quantity: defaultQuantity,
      dueDate: defaultDueDate,
      notes: defaultNotes,
      pricingTier: student.pricingTier,
    }));

    setAssessmentDrafts(drafts);
    setErrorMessage('');
  };

  const handleUpdateDraft = (draftId: string, field: keyof AssessmentDraft, value: any) => {
    setAssessmentDrafts(prev =>
      prev.map(draft =>
        draft.id === draftId ? { ...draft, [field]: value } : draft
      )
    );
  };

  const handleRemoveDraft = (draftId: string) => {
    setAssessmentDrafts(prev => prev.filter(draft => draft.id !== draftId));
  };

  const handleConfirmAssessments = () => {
    assessmentDrafts.forEach(draft => {
      const newAssessment: PreAssessedFee = {
        id: uuidv4(),
        studentId: draft.studentId,
        feeId: selectedFee!.id,
        quantity: draft.quantity,
        dueDate: new Date(draft.dueDate),
        status: 'pending' as FeeStatus,
        pricingTier: draft.pricingTier || draft.student.pricingTier,
        createdAt: new Date(),
      };
      mockPreAssessedFees.push(newAssessment);
    });

    setSuccessMessage(`Successfully assessed ${assessmentDrafts.length} fee(s) to students.`);

    setTimeout(() => {
      setSuccessMessage('');
      handleReset();
    }, 3000);
  };

  const handleDownloadTemplate = () => {
    const csv = mockStudents.slice(0, 5).map(s => s.studentIdNumber).join(',');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student-ids-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getPriceForStudent = (fee: Fee, tier: PricingTier): number => {
    switch (tier) {
      case 'full': return fee.basePrice;
      case 'reduced': return fee.reducedPrice;
      case 'free': return fee.freePrice;
      default: return fee.basePrice;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Fee Assessment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Follow the steps below to assess fees to students
        </Typography>
      </Box>

      <Box sx={{ height: successMessage || errorMessage ? 'auto' : 0, mb: (successMessage || errorMessage) ? 3 : 0, overflow: 'hidden', transition: 'margin 0.2s ease' }}>
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="warning" onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                      mt: 1,
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Select Fee
              </Typography>
              <Autocomplete
                options={activeFees}
                getOptionLabel={(option) => option.name}
                value={selectedFee}
                onChange={(_e, newValue) => setSelectedFee(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Fee" placeholder="Search fees..." />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${option.basePrice.toFixed(2)} - {option.course || 'General'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />

              {selectedFee && (
                <Box sx={{ mt: 3, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {selectedFee.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Full: $${selectedFee.basePrice.toFixed(2)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`Reduced: $${selectedFee.reducedPrice.toFixed(2)}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    <Chip
                      label={`Free: $${selectedFee.freePrice.toFixed(2)}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Select Students
              </Typography>

              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab
                  icon={<Filter size={18} />}
                  label="Filter & Select"
                  iconPosition="start"
                  sx={{ minWidth: 'auto' }}
                />
                <Tab
                  icon={<Users size={18} />}
                  label="Search"
                  iconPosition="start"
                  sx={{ minWidth: 'auto' }}
                />
                <Tab
                  icon={<FileText size={18} />}
                  label="Paste IDs"
                  iconPosition="start"
                  sx={{ minWidth: 'auto' }}
                />
                <Tab
                  icon={<Upload size={18} />}
                  label="CSV Upload"
                  iconPosition="start"
                  sx={{ minWidth: 'auto' }}
                />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Use the filters below to find students by grade and school, then select all matching students at once.
                </Alert>
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 3,
                }}>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
                    <InputLabel>Grade</InputLabel>
                    <Select
                      value={gradeFilter}
                      onChange={(e) => setGradeFilter(e.target.value)}
                      label="Grade"
                    >
                      <MenuItem value="all">All Grades</MenuItem>
                      {[...Array(12)].map((_, i) => (
                        <MenuItem key={i + 1} value={(i + 1).toString()}>
                          Grade {i + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel>School</InputLabel>
                    <Select
                      value={schoolFilter}
                      onChange={(e) => setSchoolFilter(e.target.value)}
                      label="School"
                    >
                      <MenuItem value="all">All Schools</MenuItem>
                      {schools.map(school => (
                        <MenuItem key={school.id} value={school.id}>
                          {school.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {filteredStudents.length} students match current filters
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      onClick={() => setSelectedStudents(filteredStudents)}
                      disabled={filteredStudents.length === 0}
                      sx={darkButtonSx}
                    >
                      Select All {filteredStudents.length} Students
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedStudents([])}
                      disabled={selectedStudents.length === 0}
                      sx={{
                        borderColor: '#1e293b',
                        color: '#1e293b',
                        '&:hover': {
                          borderColor: '#0f172a',
                          backgroundColor: 'rgba(15, 23, 42, 0.04)',
                        },
                      }}
                    >
                      Clear Selection
                    </Button>
                  </Stack>
                </Paper>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Search and select individual students from the list below.
                </Alert>
                <Autocomplete
                  multiple
                  options={filteredStudents}
                  getOptionLabel={(option) => `${option.studentIdNumber} - ${option.firstName} ${option.lastName}`}
                  value={selectedStudents}
                  onChange={(_e, newValue) => setSelectedStudents(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Students" placeholder="Search students..." />
                  )}
                  renderOption={(props, option) => {
                    const schoolName = getSchoolById(option.schoolId)?.name || 'Unknown';
                    return (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.firstName} {option.lastName} ({option.studentIdNumber})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Grade {option.grade} - {option.pricingTier} tier - {schoolName}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={`${option.firstName} ${option.lastName}`}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                  }
                />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Paste student ID numbers separated by commas.
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Example: S2024001, S2024002, S2024003, S2024004, S2024005"
                  value={pasteInput}
                  onChange={(e) => setPasteInput(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  onClick={handleProcessPaste}
                  disabled={!pasteInput.trim()}
                  startIcon={<Check size={18} />}
                  sx={darkButtonSx}
                >
                  Process IDs
                </Button>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Upload a CSV file with comma-separated student ID numbers.
                </Alert>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<Download size={18} />}
                    onClick={handleDownloadTemplate}
                    sx={{
                      borderColor: '#1e293b',
                      color: '#1e293b',
                      '&:hover': {
                        borderColor: '#0f172a',
                        backgroundColor: 'rgba(15, 23, 42, 0.04)',
                      },
                    }}
                  >
                    Download Template
                  </Button>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload size={18} />}
                    sx={{
                      borderColor: '#1e293b',
                      color: '#1e293b',
                      '&:hover': {
                        borderColor: '#0f172a',
                        backgroundColor: 'rgba(15, 23, 42, 0.04)',
                      },
                    }}
                  >
                    Choose CSV File
                    <input
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                </Stack>
                {csvFile && (
                  <Box sx={{ mb: 2 }}>
                    <Chip label={csvFile.name} onDelete={() => setCsvFile(null)} />
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={handleProcessCSV}
                  disabled={!csvFile}
                  startIcon={<Check size={18} />}
                  sx={darkButtonSx}
                >
                  Process CSV
                </Button>
              </TabPanel>

              {selectedStudents.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Selected Students ({selectedStudents.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>Student ID</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>Name</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Grade</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>School</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>Tier</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedStudents.slice(0, 10).map((student) => (
                          <TableRow key={student.id}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{student.studentIdNumber}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{student.firstName} {student.lastName}</TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{student.grade}</TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{getSchoolById(student.schoolId)?.name || 'Unknown'}</TableCell>
                            <TableCell>
                              <Chip label={student.pricingTier} size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {selectedStudents.length > 10 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Showing 10 of {selectedStudents.length} students
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Configure Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set default values for the fee assessments
              </Typography>

              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={defaultQuantity}
                onChange={(e) => setDefaultQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={defaultDueDate}
                onChange={(e) => setDefaultDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={4}
                value={defaultNotes}
                onChange={(e) => setDefaultNotes(e.target.value)}
                placeholder="Add notes about this assessment..."
              />
            </Box>
          )}

          {activeStep === 3 && (
            <Box>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
              }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Review & Confirm
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {assessmentDrafts.length} assessment(s) for {selectedFee?.name}
                  </Typography>
                </Box>
                <Chip
                  label={`Total: $${assessmentDrafts.reduce((sum, draft) =>
                    sum + (getPriceForStudent(selectedFee!, draft.pricingTier || draft.student.pricingTier) * draft.quantity), 0
                  ).toFixed(2)}`}
                  color="primary"
                  size="medium"
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Review and edit the assessments below before confirming. You can adjust quantity, due date, pricing tier, and notes for each student.
              </Alert>

          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Student</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Qty</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Tier</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Unit Price</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Total</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Due Date</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Notes</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentDrafts.map((draft) => {
                  const unitPrice = getPriceForStudent(selectedFee!, draft.pricingTier || draft.student.pricingTier);
                  return (
                    <TableRow key={draft.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {draft.student.firstName} {draft.student.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {draft.student.studentIdNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={draft.quantity}
                          onChange={(e) => handleUpdateDraft(draft.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                          inputProps={{ min: 1, style: { width: '60px' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={draft.pricingTier || draft.student.pricingTier}
                            onChange={(e) => handleUpdateDraft(draft.id, 'pricingTier', e.target.value)}
                          >
                            <MenuItem value="full">Full</MenuItem>
                            <MenuItem value="reduced">Reduced</MenuItem>
                            <MenuItem value="free">Free</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>${unitPrice.toFixed(2)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                          ${(unitPrice * draft.quantity).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          value={draft.dueDate}
                          onChange={(e) => handleUpdateDraft(draft.id, 'dueDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ minWidth: 140 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={draft.notes || ''}
                          onChange={(e) => handleUpdateDraft(draft.id, 'notes', e.target.value)}
                          placeholder="Notes..."
                          sx={{ minWidth: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveDraft(draft.id)}
                          color="error"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

              {assessmentDrafts.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <AlertCircle size={48} style={{ color: '#999' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No assessments to review
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            gap: 2,
          }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowLeft size={18} />}
              sx={{
                borderColor: '#1e293b',
                color: '#1e293b',
                '&:hover': {
                  borderColor: '#0f172a',
                  backgroundColor: 'rgba(15, 23, 42, 0.04)',
                },
              }}
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
              {activeStep === 3 ? (
                permissions.create && (
                  <Button
                    variant="contained"
                    onClick={handleConfirmAssessments}
                    disabled={assessmentDrafts.length === 0}
                    startIcon={<Check size={18} />}
                    sx={{ ...darkButtonSx, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Confirm & Assess Fees
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowRight size={18} />}
                  sx={{ ...darkButtonSx, width: { xs: '100%', sm: 'auto' } }}
                >
                  {activeStep === 2 ? 'Review' : 'Next'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
